import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Edit, RefreshCw, Clock, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const DraftEntries = () => {
  const navigate = useNavigate();
  const [inwardDrafts, setInwardDrafts] = useState([]);
  const [outwardDrafts, setOutwardDrafts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('inward');

  useEffect(() => {
    fetchDrafts();
  }, []);

  const fetchDrafts = async () => {
    setIsLoading(true);
    try {
      const [inwardResponse, outwardResponse] = await Promise.all([
        apiClient.get('/inward/drafts'),
        apiClient.get('/outward/drafts')
      ]);
      setInwardDrafts(inwardResponse.data.entries || []);
      setOutwardDrafts(outwardResponse.data.entries || []);
    } catch (error) {
      console.error('Error fetching drafts:', error);
      toast.error('Failed to load draft entries');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditInward = (draft) => {
    navigate('/gate-entry/inward', { state: { draftEntry: draft } });
  };

  const handleEditOutward = (draft) => {
    navigate('/gate-entry/outward', { state: { draftEntry: draft } });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch {
      return timestamp;
    }
  };

  const totalDrafts = inwardDrafts.length + outwardDrafts.length;

  const renderDraftTable = (drafts, type) => {
    if (drafts.length === 0) {
      return (
        <div className="text-center py-12">
          <Clock className="w-12 h-12 text-[#E5DFD0] mx-auto mb-4" />
          <p className="text-[#5A5F6E]">No {type} draft entries found</p>
          <p className="text-sm text-[#8A8F9E] mt-1">
            Drafts will appear here when you save an entry without submitting
          </p>
          <Button
            onClick={() => navigate(type === 'inward' ? '/gate-entry/inward' : '/gate-entry/outward')}
            className="mt-4 bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
          >
            Create New {type === 'inward' ? 'Inward' : 'Outward'} Entry
          </Button>
        </div>
      );
    }

    return (
      <div className="border border-[#E5DFD0] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F5F1E8] hover:bg-[#F5F1E8]">
                <TableHead className="font-semibold text-[#1A1F2E]">Entry ID</TableHead>
                <TableHead className="font-semibold text-[#1A1F2E]">Document Barcode</TableHead>
                <TableHead className="font-semibold text-[#1A1F2E]">Staff ID</TableHead>
                {type === 'outward' && (
                  <TableHead className="font-semibold text-[#1A1F2E]">Destination</TableHead>
                )}
                <TableHead className="font-semibold text-[#1A1F2E]">Bundles</TableHead>
                <TableHead className="font-semibold text-[#1A1F2E]">Created</TableHead>
                <TableHead className="font-semibold text-[#1A1F2E] text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {drafts.map((draft) => (
                <TableRow key={draft.entry_id} className="hover:bg-[#FFFBF5]">
                  <TableCell className="font-mono text-sm font-medium text-[#1A1F2E]">
                    {draft.entry_id}
                  </TableCell>
                  <TableCell className="font-mono text-sm text-[#5A5F6E]">
                    {draft.document_barcode}
                  </TableCell>
                  <TableCell className="text-sm text-[#5A5F6E]">
                    {draft.staff_id}
                  </TableCell>
                  {type === 'outward' && (
                    <TableCell className="text-sm text-[#5A5F6E]">
                      {draft.destination || '-'}
                    </TableCell>
                  )}
                  <TableCell className="text-sm text-[#5A5F6E]">
                    {draft.bundles?.length || 0}
                  </TableCell>
                  <TableCell className="text-sm text-[#5A5F6E]">
                    {formatDateTime(draft.timestamp)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => type === 'inward' ? handleEditInward(draft) : handleEditOutward(draft)}
                        className="h-8 px-3 border-[#2D5F4F] text-[#2D5F4F] hover:bg-[#2D5F4F] hover:text-white"
                        data-testid={`edit-draft-${draft.entry_id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Continue
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Draft Entries</h1>
              <p className="text-sm text-[#E8DCC8]">Continue editing your drafts</p>
            </div>
          </div>
          <Button onClick={fetchDrafts} variant="secondary" className="h-10" disabled={isLoading} data-testid="refresh-drafts">
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
          <div className="flex items-center gap-2 mb-6">
            <FileText className="w-5 h-5 text-[#F59E0B]" />
            <h2 className="text-lg font-semibold text-[#1A1F2E] font-display">
              Your Drafts ({totalDrafts})
            </h2>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" />
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger 
                  value="inward" 
                  className="flex items-center gap-2"
                  data-testid="inward-drafts-tab"
                >
                  <ArrowDownToLine className="w-4 h-4" />
                  Inward Drafts ({inwardDrafts.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="outward" 
                  className="flex items-center gap-2"
                  data-testid="outward-drafts-tab"
                >
                  <ArrowUpFromLine className="w-4 h-4" />
                  Outward Drafts ({outwardDrafts.length})
                </TabsTrigger>
              </TabsList>
              <TabsContent value="inward">
                {renderDraftTable(inwardDrafts, 'inward')}
              </TabsContent>
              <TabsContent value="outward">
                {renderDraftTable(outwardDrafts, 'outward')}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>

    </div>
  );
};

export default DraftEntries;
